import { service } from '@ember/service';
import {
  getSingleValueForTasField,
  setSingleValueForTasField,
  type FieldName,
} from 'tio-common/utils/tuition-assistance/fields';
import { buildSchoolFilter } from 'tio-common/utils/tuition-assistance/build-school-filter';
import Route from '@ember/routing/route';
import type { IntlService } from 'ember-intl';
import type RouterService from '@ember/routing/router';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasApplicationModel from 'tio-common/models/tas-application';
import type TasUnifiedInstitutionModel from 'tio-common/models/tas-unified-institution';

export default class AuthenticatedTasApplicationsTypeFourGuidedNewRoute extends Route {
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare intl: IntlService;

  includes = [
    'tas-courses',
    'tas-courses.tas-assets.attachments',
    'tas-courses.tas-assets.asset-summary',
    'tas-program-instance',
    'tas-program-instance.tas-program-template.tas-groups',
    'tas-program-instance.tas-program-template.conditions.public-assets',
    'tas-program-instance.tas-program-template.tas-field-options',
    'tas-program-instance.employee.person',
    'tas-program-instance.employee.tas-participant',
    'tas-program-instance.employee.company',
    'tas-program-instance.employee.pcs-card-holders',
    'tas-program-instance.tas-program-template.company.company-setting',
    'tas-assets.attachments',
    'tas-assets.asset-summary',
    'tas-assistances',
    'comments',
    'tags',
  ];

  queryParams = {
    date: {
      refreshModel: false,
    },
  };

  saveDate(date: string, application: TasApplicationModel, lockStatus: string) {
    const field: FieldName =
      lockStatus === 'COURSE_END_DATE' ? 'COURSES_END_DATE' : 'COURSES_BEGIN_DATE';

    const fieldValue = getSingleValueForTasField(field, application.fields);

    if (fieldValue) {
      return false;
    }
    setSingleValueForTasField(field, date, application.fields);
    return true;
  }

  async model({ tas_application_id, date = '' }: { tas_application_id: string; date?: string }) {
    const application = await this.store.findRecord<TasApplicationModel>(
      'tas-application',
      tas_application_id,
      {
        include: this.includes.join(','),
        reload: true,
      }
    );

    const programInstance = application.tasProgramInstance;

    if (date) {
      const programTemplate = programInstance.tasProgramTemplate;
      const lockStatus: string = getSingleValueForTasField(
        'LOCK_EMPLOYEE_STATUS_BASED_ON',
        programTemplate.fields
      );
      const saved: boolean = this.saveDate(date, application, lockStatus);

      if (saved) {
        try {
          await application.save();
        } catch (error) {
          console.error('Failed to save application, instance, or course:', error);
          application.rollbackAttributes();
        }
      }
    }

    let selectedSchool: TasUnifiedInstitutionModel | undefined;
    try {
      const tasInstitutionId = programInstance.fields['TAS_INSTITUTION_ID']?.values?.[0];
      const dapipInstitutionId = programInstance.fields['DAPIP_INSTITUTION_ID']?.values?.[0];

      if (tasInstitutionId || dapipInstitutionId) {
        const filter = buildSchoolFilter(programInstance);
        const results = await this.store.query<TasUnifiedInstitutionModel>(
          'tas-unified-institution',
          {
            // @ts-expect-error Ember Data query filter type mismatch
            filter,
            page: { limit: 1, offset: 0 },
          }
        );
        selectedSchool = results[0];
      }
    } catch (e) {
      console.error('Error attempting to load previously selected school', e);
    }

    try {
      await application.loadHistory();
    } catch (e) {
      // History is non-critical — don't block the route
      console.error('Error loading application history', e);
    }

    return {
      application: application,
      programInstance: programInstance,
      selectedSchool: selectedSchool,
    };
  }
}
